import React, { useState } from 'react';
import {
    LayoutDashboard, Calendar, KanbanSquare, ListTodo,
    FolderKanban, Users, BarChart3, Settings, Plus,
    ChevronDown, ChevronRight, Search, Bell, LogOut,
    Sparkles, Clock, Target, Zap, Hash, MoreHorizontal,
    Star, StarOff, ClipboardList, Rocket, Table2
} from 'lucide-react';
import { ViewMode, Project, User } from '../../types';

interface BordioSidebarProps {
    currentView: ViewMode;
    onViewChange: (view: ViewMode) => void;
    projects: Project[];
    onNewProject: () => void;
    onProjectSelect: (projectId: string) => void;
    currentUser: User | null;
    collapsed?: boolean;
    onToggleCollapse?: () => void;
}

export const BordioSidebar: React.FC<BordioSidebarProps> = ({
    currentView,
    onViewChange,
    projects,
    onNewProject,
    onProjectSelect,
    currentUser,
    collapsed = false,
}) => {
    const [projectsExpanded, setProjectsExpanded] = useState(true);
    const [favoriteProjects, setFavoriteProjects] = useState<string[]>([]);

    const toggleFavorite = (projectId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setFavoriteProjects(prev =>
            prev.includes(projectId)
                ? prev.filter(id => id !== projectId)
                : [...prev, projectId]
        );
    };

    const mainNavItems = [
        { id: 'dashboard' as ViewMode, label: 'Dashboard', icon: LayoutDashboard },
        { id: 'boards' as ViewMode, label: 'Board', icon: KanbanSquare },
        { id: 'calendar' as ViewMode, label: 'Calendar', icon: Calendar },
        { id: 'issues' as ViewMode, label: 'Tasks', icon: ListTodo },
        { id: 'sprints' as ViewMode, label: 'Sprints', icon: Target },
    ];

    const secondaryNavItems = [
        { id: 'manager-dashboard' as ViewMode, label: 'Manager', icon: ClipboardList },
        { id: 'team-analytics' as ViewMode, label: 'Analytics', icon: BarChart3 },
        { id: 'sheets' as ViewMode, label: 'Sheets', icon: Table2 },
        { id: 'deployments' as ViewMode, label: 'Deployments', icon: Rocket },
        { id: 'reports' as ViewMode, label: 'Reports', icon: Zap },
        { id: 'ai-api' as ViewMode, label: 'AI API', icon: Sparkles },
    ];

    const getProjectColor = (index: number) => {
        const colors = [
            'bg-blue-400', 'bg-orange-400', 'bg-emerald-400',
            'bg-purple-400', 'bg-teal-400', 'bg-pink-400',
            'bg-cyan-400', 'bg-rose-400'
        ];
        return colors[index % colors.length];
    };

    if (collapsed) {
        return (
            <div className="w-16 h-full bg-white border-r border-gray-200 flex flex-col items-center py-4 gap-1">
                {/* Logo */}
                <img src="/planner-logo.png" alt="Planner" className="w-9 h-9 mb-4 object-contain shrink-0" />

                {mainNavItems.map(item => (
                    <button
                        key={item.id}
                        onClick={() => onViewChange(item.id)}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 group relative ${currentView === item.id
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                        title={item.label}
                    >
                        <item.icon className="w-5 h-5" />
                    </button>
                ))}

                <div className="w-8 h-px bg-gray-200 my-2" />

                {secondaryNavItems.map(item => (
                    <button
                        key={item.id}
                        onClick={() => onViewChange(item.id)}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${currentView === item.id
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                        title={item.label}
                    >
                        <item.icon className="w-5 h-5" />
                    </button>
                ))}
            </div>
        );
    }

    return (
        <div className="w-52 h-full bg-white border-r border-gray-200 flex flex-col">
            {/* Logo & Workspace */}
            <div className="px-4 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <img src="/planner-logo.png" alt="Planner" className="w-9 h-9 object-contain shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h1 className="text-sm font-bold text-gray-900 truncate">Planner</h1>
                        <p className="text-2xs text-gray-500 truncate">
                            {currentUser?.displayName || 'Workspace'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="px-3 py-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-gray-500 cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors">
                    <Search className="w-4 h-4" />
                    <span className="text-xs">Search...</span>
                    <kbd className="ml-auto text-2xs bg-white px-1.5 py-0.5 rounded border border-gray-200 text-gray-500">⌘K</kbd>
                </div>
            </div>

            {/* Main Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-1 scrollbar-hide">
                <div className="space-y-0.5">
                    {mainNavItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => onViewChange(item.id)}
                            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer ${currentView === item.id
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                        >
                            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>

                {/* Divider */}
                <div className="my-3 h-px bg-gray-200" />

                {/* Projects Section */}
                <div>
                    <button
                        onClick={() => setProjectsExpanded(!projectsExpanded)}
                        className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                    >
                        <span>Projects</span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNewProject();
                                }}
                                className="w-5 h-5 rounded flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                            {projectsExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                            ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                            )}
                        </div>
                    </button>

                    {projectsExpanded && (
                        <div className="mt-1 space-y-0.5">
                            {projects.length === 0 ? (
                                <div className="px-3 py-4 text-center">
                                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                                        <FolderKanban className="w-5 h-5 text-gray-400" />
                                    </div>
                                    <p className="text-xs text-gray-500 mb-2">No projects yet</p>
                                    <button
                                        onClick={onNewProject}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        Create your first project
                                    </button>
                                </div>
                            ) : (
                                projects.map((project, index) => (
                                    <button
                                        key={project.id}
                                        onClick={() => {
                                            onProjectSelect(project.id);
                                            onViewChange('projects');
                                        }}
                                        className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all duration-150 cursor-pointer group"
                                    >
                                        <div className={`w-5 h-5 ${getProjectColor(index)} rounded flex items-center justify-center flex-shrink-0`}>
                                            <span className="text-white text-2xs font-bold">
                                                {project.key?.charAt(0) || project.name.charAt(0)}
                                            </span>
                                        </div>
                                        <span className="truncate text-sm">{project.name}</span>
                                        <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => toggleFavorite(project.id, e)}
                                                className="w-5 h-5 rounded flex items-center justify-center hover:bg-gray-200 transition-colors"
                                            >
                                                {favoriteProjects.includes(project.id) ? (
                                                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                                ) : (
                                                    <StarOff className="w-3 h-3 text-gray-400" />
                                                )}
                                            </button>
                                            <button
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-5 h-5 rounded flex items-center justify-center hover:bg-gray-200 transition-colors"
                                            >
                                                <MoreHorizontal className="w-3 h-3 text-gray-400" />
                                            </button>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Divider */}
                <div className="my-3 h-px bg-gray-200" />

                {/* Secondary Navigation */}
                <div className="space-y-0.5">
                    {secondaryNavItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => onViewChange(item.id)}
                            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer ${currentView === item.id
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                        >
                            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>
            </nav>

            {/* Bottom section */}
            <div className="border-t border-gray-200 p-3">
                <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors group">
                    {currentUser?.avatarUrl ? (
                        <img
                            src={currentUser.avatarUrl}
                            alt={currentUser.displayName}
                            className="w-8 h-8 rounded-full object-cover ring-2 ring-gray-200"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                            {currentUser?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                            {currentUser?.displayName || 'User'}
                        </p>
                        <p className="text-2xs text-gray-500 truncate">
                            {currentUser?.emailAddress || ''}
                        </p>
                    </div>
                    <Settings className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
            </div>
        </div>
    );
};
